import { useEffect, useState } from "react";
import { ExternalLink, Tractor } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { MaskedLine, FadeUp, Kicker } from "@/components/Reveal";

export default function Attrezzi() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Attrezzi consigliati — Luca Mannu Giardini";
    api.get("/agri-products").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main className="pt-32" data-testid="attrezzi-page">
      <header className="mx-auto max-w-7xl px-6 lg:px-10">
        <Kicker>La mia attrezzatura</Kicker>
        <h1 className="mt-4 font-serif text-5xl tracking-tight text-forest md:text-6xl">
          <MaskedLine delay={0.1}>Gli attrezzi che</MaskedLine>
          <MaskedLine delay={0.22} className="italic text-moss">uso davvero.</MaskedLine>
        </h1>
        <FadeUp delay={0.35}>
          <p className="mt-6 max-w-xl leading-relaxed text-sage">
            Macchine e attrezzi che ho scelto, provato e approvato sui miei cantieri. I pulsanti portano ad AgriEuro, dove li compro io stesso.
          </p>
        </FadeUp>
      </header>

      <section className="mx-auto max-w-7xl px-6 pb-32 pt-16 lg:px-10">
        {loading && <p className="text-sage" data-testid="attrezzi-loading">Caricamento…</p>}
        {!loading && items.length === 0 && <p className="text-sage" data-testid="attrezzi-empty">Nessun attrezzo pubblicato ancora.</p>}
        <div className="grid gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-4%" }}
              transition={{ duration: 0.85, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group flex flex-col"
              data-testid={`agri-card-${p.id}`}
            >
              <div className="arch-frame aspect-[4/3] overflow-hidden bg-sand">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
              <h2 className="mt-6 font-serif text-2xl leading-snug text-forest">{p.title}</h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-sage">{p.description}</p>
              {p.link ? (
                <a
                  href={p.link}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  data-testid={`agri-link-${p.id}`}
                  className="mt-6 flex w-fit items-center gap-2 rounded-full bg-terra px-6 py-3 font-accent text-xs uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-105 active:scale-95"
                >
                  <Tractor className="h-4 w-4" strokeWidth={1.5} /> Vedi su AgriEuro <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                </a>
              ) : (
                <span className="mt-6 w-fit cursor-not-allowed rounded-full border border-line px-6 py-3 font-accent text-xs uppercase tracking-[0.2em] text-sage" data-testid={`agri-link-disabled-${p.id}`}>
                  Link in arrivo
                </span>
              )}
            </motion.article>
          ))}
        </div>
      </section>
    </main>
  );
}
